/**
 * PropertyAdminOffersTab
 * ----------------------
 * Buying-side tab where an agent (as a buyer) can:
 *   - See every property they've saved (favourited)
 *   - Review the offers they've already made on each (amount + date + status)
 *   - Submit a new offer, which triggers a Resend email to the owner-agent
 *
 * Layout: one card per saved property; offer history stacked underneath;
 * "Make Offer" button opens a dialog with amount + optional message.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { getApiUrl } from "@/utils/api";
import { useFavourites } from "@/hooks/useFavourites";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, HandCoins, Heart, Trash2 } from "lucide-react";

const API = getApiUrl();

function formatAmount(amount, currency = "£") {
  if (amount == null || amount === "") return "—";
  const n = Number(amount);
  return `${currency}${n.toLocaleString(undefined, {
    maximumFractionDigits: n >= 1000 ? 0 : 2,
  })}`;
}

function OfferRow({ offer, onWithdraw, busy }) {
  const status = offer.status || "sent";
  const badgeCls =
    status === "accepted"
      ? "bg-green-100 text-green-700"
      : status === "rejected"
      ? "bg-red-100 text-red-700"
      : status === "withdrawn"
      ? "bg-slate-100 text-slate-600"
      : "bg-amber-100 text-amber-700";
  const canWithdraw = status === "sent";
  return (
    <div
      className="flex items-center justify-between gap-3 border-t border-border px-3 py-2 text-sm"
      data-testid={`offer-row-${offer.offer_id}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-semibold">
            {formatAmount(offer.amount, offer.currency || "£")}
          </span>
          <span className={`text-[10px] px-2 py-0.5 rounded ${badgeCls} capitalize`}>
            {status}
          </span>
          {status === "sent" && (
            offer.email_sent ? (
              <span className="text-[10px] text-muted-foreground">Emailed owner</span>
            ) : (
              <span className="text-[10px] text-amber-700">Email pending</span>
            )
          )}
        </div>
        <div className="text-xs text-muted-foreground">
          {offer.created_at
            ? new Date(offer.created_at).toLocaleString()
            : ""}
        </div>
        {(offer.buying_position || offer.move_timeline) && (
          <div className="text-[11px] text-muted-foreground mt-1 flex flex-wrap gap-2" data-testid={`offer-position-${offer.offer_id}`}>
            {offer.buying_position && (
              <span className="px-1.5 py-0.5 bg-muted rounded">
                {offer.buying_position}
              </span>
            )}
            {offer.move_timeline && (
              <span className="px-1.5 py-0.5 bg-muted rounded">
                Move: {offer.move_timeline}
              </span>
            )}
          </div>
        )}
        {offer.message ? (
          <div className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap line-clamp-2">
            “{offer.message}”
          </div>
        ) : null}
        {status === "accepted" && offer.decision_note ? (
          <div className="text-xs mt-1 whitespace-pre-wrap bg-green-50 border-l-2 border-green-400 pl-2 py-1">
            <span className="text-green-700 font-medium">Owner accepted: </span>
            {offer.decision_note}
          </div>
        ) : null}
        {status === "rejected" && offer.decision_note ? (
          <div className="text-xs mt-1 whitespace-pre-wrap bg-red-50 border-l-2 border-red-400 pl-2 py-1">
            <span className="text-red-700 font-medium">Owner rejected: </span>
            {offer.decision_note}
          </div>
        ) : null}
        {status === "withdrawn" && offer.withdrawn_at ? (
          <div className="text-[11px] text-muted-foreground mt-1">
            Withdrawn on {new Date(offer.withdrawn_at).toLocaleString()}
          </div>
        ) : null}
      </div>
      {canWithdraw && (
        <Button
          size="sm"
          variant="ghost"
          className="rounded-none h-7 text-xs text-red-600 hover:bg-red-50"
          disabled={busy}
          onClick={() => onWithdraw(offer)}
          data-testid={`offer-withdraw-${offer.offer_id}`}
        >
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : (
            <>
              <Trash2 className="h-3 w-3 mr-1" /> Withdraw
            </>
          )}
        </Button>
      )}
    </div>
  );
}

const BUYING_POSITIONS = [
  "Cash buyer",
  "Cash + Mortgage",
  "Mortgage agreed in principle",
  "First-time buyer",
  "In a chain",
  "Chain-free (property sold)",
  "Property to sell",
  "Other",
];

const MOVE_TIMELINES = [
  "ASAP",
  "1–3 months",
  "3–6 months",
  "6–12 months",
  "12+ months",
  "Flexible",
];

function MakeOfferDialog({ open, onOpenChange, property, onSubmitted }) {
  const [amount, setAmount] = useState("");
  const [message, setMessage] = useState("");
  const [buyingPosition, setBuyingPosition] = useState("");
  const [moveTimeline, setMoveTimeline] = useState("");
  const [positionNotes, setPositionNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setAmount("");
      setMessage("");
      setBuyingPosition("");
      setMoveTimeline("");
      setPositionNotes("");
    }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    const n = Number(amount);
    if (!n || n <= 0) {
      toast.error("Enter a valid offer amount");
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${API}/property-admin/offers`,
        {
          property_id: property.property_id,
          amount: n,
          message,
          buying_position: buyingPosition,
          move_timeline: moveTimeline,
          position_notes: positionNotes,
        },
        { withCredentials: true }
      );
      const emailed = data?.offer?.email_sent;
      toast.success(
        emailed
          ? "Offer sent — owner has been emailed"
          : "Offer saved — owner email pending"
      );
      onSubmitted();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit offer");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md rounded-none max-h-[90vh] overflow-y-auto"
        data-testid="make-offer-dialog"
      >
        <DialogHeader>
          <DialogTitle>Make an offer</DialogTitle>
          <DialogDescription>
            {property?.title || "Property"} — listed at{" "}
            {formatAmount(property?.price, property?.currency || "£")}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="offer-amount" className="text-xs">
              Offer amount ({property?.currency || "£"})
            </Label>
            <Input
              id="offer-amount"
              type="number"
              min={0}
              step="1"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="rounded-none mt-1"
              placeholder="e.g. 250000"
              autoFocus
              data-testid="offer-amount-input"
            />
          </div>
          <div className="grid grid-cols-1 gap-3">
            <div>
              <Label htmlFor="offer-buying-position" className="text-xs">
                Your buying position
              </Label>
              <select
                id="offer-buying-position"
                value={buyingPosition}
                onChange={(e) => setBuyingPosition(e.target.value)}
                className="mt-1 w-full text-sm border border-input rounded-none px-3 py-2 bg-background"
                data-testid="offer-buying-position-select"
              >
                <option value="">— Select —</option>
                {BUYING_POSITIONS.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="offer-move-timeline" className="text-xs">
                When do you want to move?
              </Label>
              <select
                id="offer-move-timeline"
                value={moveTimeline}
                onChange={(e) => setMoveTimeline(e.target.value)}
                className="mt-1 w-full text-sm border border-input rounded-none px-3 py-2 bg-background"
                data-testid="offer-move-timeline-select"
              >
                <option value="">— Select —</option>
                {MOVE_TIMELINES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="offer-position-notes" className="text-xs">
                Position notes (optional)
              </Label>
              <Textarea
                id="offer-position-notes"
                value={positionNotes}
                onChange={(e) => setPositionNotes(e.target.value)}
                className="rounded-none mt-1 min-h-[60px]"
                placeholder="e.g. Mortgage in principle from HSBC, chain of 2 above"
                data-testid="offer-position-notes-input"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="offer-message" className="text-xs">
              Message to owner (optional)
            </Label>
            <Textarea
              id="offer-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="rounded-none mt-1 min-h-[80px]"
              placeholder="Anything else the owner should know…"
              data-testid="offer-message-input"
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              className="rounded-none"
              onClick={() => onOpenChange(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="rounded-none"
              disabled={saving}
              data-testid="offer-submit-btn"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <HandCoins className="h-4 w-4 mr-2" /> Send offer
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function PropertyAdminOffersTab({ linkedProperties = [] }) {
  const mineIds = new Set(linkedProperties.map((p) => p.property_id));
  const { items: favourites, loading: favouritesLoading } = useFavourites();
  const [offers, setOffers] = useState([]);
  const [offersLoading, setOffersLoading] = useState(true);
  const [dialogProperty, setDialogProperty] = useState(null);
  const [busyOfferId, setBusyOfferId] = useState(null);

  const loadOffers = async () => {
    setOffersLoading(true);
    try {
      const { data } = await axios.get(`${API}/property-admin/offers`, {
        withCredentials: true,
      });
      setOffers(data?.offers || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load offers");
    } finally {
      setOffersLoading(false);
    }
  };

  useEffect(() => {
    loadOffers();
  }, []);

  const offersByProperty = useMemo(() => {
    const m = new Map();
    for (const o of offers) {
      if (!m.has(o.property_id)) m.set(o.property_id, []);
      m.get(o.property_id).push(o);
    }
    return m;
  }, [offers]);

  const withdrawOffer = async (offer) => {
    if (!window.confirm("Withdraw this offer? The owner will be notified.")) return;
    setBusyOfferId(offer.offer_id);
    try {
      await axios.post(
        `${API}/property-admin/offers/${offer.offer_id}/withdraw`,
        {},
        { withCredentials: true }
      );
      toast.success("Offer withdrawn — owner notified");
      await loadOffers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to withdraw offer");
    } finally {
      setBusyOfferId(null);
    }
  };

  const loading = favouritesLoading || offersLoading;

  // Also include properties that have offers but are no longer favourited,
  // so historical offers remain visible.
  const propertyMap = new Map();
  for (const f of favourites) {
    if (f.property) propertyMap.set(f.property_id, f.property);
    else propertyMap.set(f.property_id, { property_id: f.property_id });
  }
  for (const o of offers) {
    if (!propertyMap.has(o.property_id) && o.property) {
      propertyMap.set(o.property_id, o.property);
    }
  }
  const propertyList = Array.from(propertyMap.values());

  return (
    <div className="bg-white border border-border p-6" data-testid="offers-tab-content">
      <h2 className="text-xl font-semibold flex items-center gap-2 mb-1">
        <HandCoins className="h-5 w-5" /> Offers made
      </h2>
      <p className="text-sm text-muted-foreground mb-6">
        Your offer history on the properties you&apos;ve saved.
      </p>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : propertyList.length === 0 ? (
        <div
          className="border border-dashed border-border rounded-lg p-8 text-center text-sm text-muted-foreground bg-muted/20"
          data-testid="offers-empty"
        >
          <Heart className="h-8 w-8 mx-auto mb-2 text-muted-foreground/60" />
          You haven&apos;t saved any properties yet.{" "}
          <Link to="/properties" className="text-primary hover:underline font-medium">
            Browse properties →
          </Link>
        </div>
      ) : (
        <ul className="grid grid-cols-1 gap-4" data-testid="offers-property-list">
          {propertyList.map((p) => {
            const img = (p.gallery || [])[0]?.media_url || "";
            const propertyOffers = offersByProperty.get(p.property_id) || [];
            return (
              <li
                key={p.property_id}
                className="border border-border"
                data-testid={`offers-property-${p.property_id}`}
              >
                <div className="flex gap-4 p-4">
                  <Link
                    to={`/property/${p.property_id}`}
                    className="w-24 h-24 bg-muted flex-shrink-0 overflow-hidden bg-cover bg-center"
                    style={img ? { backgroundImage: `url(${img})` } : {}}
                    aria-label={p.title}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <Link
                          to={`/property/${p.property_id}`}
                          className="font-serif text-base font-medium hover:underline block truncate"
                        >
                          {p.title || "Property"}
                        </Link>
                        {p.display_address && (
                          <div className="text-xs text-muted-foreground truncate">
                            {p.display_address}
                          </div>
                        )}
                        <div className="text-sm mt-1">
                          Listed at{" "}
                          <span className="font-semibold">
                            {formatAmount(p.price, p.currency || "£")}
                          </span>
                        </div>
                      </div>
                      {mineIds.has(p.property_id) ? (
                        <span
                          className="text-[10px] font-semibold uppercase tracking-wide px-2 py-1 rounded bg-primary/10 text-primary border border-primary/20 whitespace-nowrap"
                          data-testid={`make-offer-own-badge-${p.property_id}`}
                        >
                          Your listing
                        </span>
                      ) : (
                        <Button
                          size="sm"
                          className="rounded-none whitespace-nowrap"
                          onClick={() => setDialogProperty(p)}
                          data-testid={`make-offer-btn-${p.property_id}`}
                        >
                          <HandCoins className="h-3 w-3 mr-1" /> Make Offer
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
                {propertyOffers.length > 0 && (
                  <div className="bg-muted/20 border-t border-border" data-testid={`offers-history-${p.property_id}`}>
                    <div className="px-3 py-2 flex items-center flex-wrap gap-2">
                      <span className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">
                        Your offers ({propertyOffers.length})
                      </span>
                      {(() => {
                        const counts = propertyOffers.reduce((acc, o) => {
                          const s = o.status || "sent";
                          acc[s] = (acc[s] || 0) + 1;
                          return acc;
                        }, {});
                        const styles = {
                          sent: "bg-amber-100 text-amber-700 border-amber-200",
                          accepted: "bg-green-100 text-green-700 border-green-200",
                          rejected: "bg-red-100 text-red-700 border-red-200",
                          withdrawn: "bg-slate-100 text-slate-600 border-slate-200",
                        };
                        return ["accepted", "sent", "rejected", "withdrawn"]
                          .filter((s) => counts[s])
                          .map((s) => (
                            <span
                              key={s}
                              className={`text-[10px] px-2 py-0.5 rounded border capitalize ${styles[s]}`}
                              data-testid={`offers-summary-${p.property_id}-${s}`}
                            >
                              {counts[s]} {s}
                            </span>
                          ));
                      })()}
                    </div>
                    {propertyOffers.map((o) => (
                      <OfferRow
                        key={o.offer_id}
                        offer={o}
                        onWithdraw={withdrawOffer}
                        busy={busyOfferId === o.offer_id}
                      />
                    ))}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <MakeOfferDialog
        open={!!dialogProperty}
        onOpenChange={(o) => { if (!o) setDialogProperty(null); }}
        property={dialogProperty || {}}
        onSubmitted={loadOffers}
      />
    </div>
  );
}
